import { Component, Element, Event, EventEmitter, h, Host, Method, Prop, State, Watch } from "@stencil/core";
import {
  RadioButtonGroupLabelFormat,
  RadioButtonSchemeDefinition,
  RadioButtonSchemeName
} from "./radio-button/constants/radio-button-types";
import {
  FormComponent,
  handleCustomValidationDelegate,
  renderRelevantNecessity,
  validateDelegate,
  validateRequiredValueDelegate
} from "../../common/form-component";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { RADIO_BUTTON_SCHEME_CONFIG, RADIO_BUTTON_SCHEME_MAP } from "./radio-button/constants/radio-button-constants";
import {
  DefaultLocalization,
  LocalizableComponent,
  Localization,
  setCurrentLocalizationDelegate
} from "../../common/localizable-component";
import pfx from "../../utils/style-class-prefix-helper";
import { uniqueId } from "../../utils/unique-id";
import { FORM_LOCALIZATION } from "../form/constants/form-i18n";
import { RadioButtonSchemeConfiguration } from "../../constants/colors";
import { BackgroundColor } from "../background/constants/background-types";
import { StyledComponent } from "../../decorators/styled-component";
import { FormFieldNecessity } from "../form/constants/form-types";
import { FORM_FIELD_NECESSITY_NONE } from "../form/constants/form-constants";

// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot [unnamed] - Holds any number of eon-ui-radio-button elements and others
 */
@Component({
  tag: "envc-eon-ui-radio-button-group",
  styleUrl: "radio-button-group.scss",
  shadow: true
})
export class RadioButtonGroup
  implements
    FormComponent<string, DefaultLocalization>,
    LocalizableComponent<DefaultLocalization>,
    ColorableComponent<RadioButtonSchemeConfiguration<RadioButtonSchemeDefinition>> {
  // @InitLogging() sentryTransaction;
  schemeConfig: RadioButtonSchemeConfiguration<RadioButtonSchemeDefinition> = RADIO_BUTTON_SCHEME_CONFIG;
  schemeMap = RADIO_BUTTON_SCHEME_MAP;

  localization: Localization<DefaultLocalization> = FORM_LOCALIZATION;
  currentLocalization: DefaultLocalization;
  initialValueState: boolean = false;

  private unfilteredRadioButtons: HTMLEnvcEonUiRadioButtonElement[];
  private assignedRadioButtons: HTMLEnvcEonUiRadioButtonElement[];
  private radioButtons: HTMLEnvcEonUiRadioButtonElement[];
  private internalId: string;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEnvcEonUiRadioButtonGroupElement;

  /**
   * Raised after the RadioButton has been clicked. Returns a boolean whether the RadioButton is now checked or not.
   */
  @Event() valueChanged: EventEmitter<string>;

  /**
   * Raised when the radio button group has been loaded.
   */
  @Event() radioButtonGroupLoad: EventEmitter;

  @State() activeScheme: RadioButtonSchemeDefinition;

  /**
   * **Internal use only!**
   * &nbsp;<br>
   * Please don't change this.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) isFormComponent: boolean = true;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop() scheme: RadioButtonSchemeName;

  /**
   * Holds the value of the current checked radioButton.
   */
  @Prop({ reflect: true, mutable: true }) value: string;

  /**
   * Property  to store the formatted value
   */
  @Prop({ reflect: true, mutable: true }) formattedValue: string = "";

  /**
   * A property to set the legend title of the RadioButtonGroup.
   * If not set, the legend will not appear.
   */
  @Prop({ mutable: true }) label: string = "";

  /**
   * Define the size of the input element
   */
  @Prop({ mutable: true }) size: "small" | "normal" = "normal";

  /**
   * A property to set the name attribute of every child element.
   */
  @Prop() name: string;

  /**
   * id of an element that describes the input element
   */
  @Prop({ attribute: "a11y-describedby", mutable: true }) a11yDescribedby: string;

  /**
   * A property to set every child Element to disabled.
   */
  @Prop({ reflect: true, mutable: true }) disabled: boolean = false;

  /**
   * A property to set the group to required
   */
  @Prop() required: boolean;

  /**
   * **Internal use only! Please don't use this property directly.**
   * &nbsp;<br>
   * A property to set every child Element to invalid.
   */
  @Prop({ reflect: true, mutable: true }) invalid: boolean;

  /**
   * ** A dedicated invalid attribute to be used in more reactive scenarios like angular, where the framework controls validation rules.**
   * &nbsp;<br>
   * @example <envc-eon-ui-input ing-invalid="true"></envc-eon-ui-input>
   */
   @Prop() showError: boolean | undefined;

  /**
   * **Internal use only! Please don't use this property directly.**
   * &nbsp;<br>
   * The error message that is displayed if the component is invalid
   */
  @Prop({ mutable: true }) errorMessage: string;

  /**
   * An empty Placeholder for an optional custom Error Message for CustomValidation.
   * @example <eon-ui-input custom-error-message="this is an custom error message"></eon-ui-input>
   */
  @Prop({ mutable: true }) customErrorMessage: string;

  /**
   * A custom message to show when the required field has no value
   */
  @Prop() customRequiredMessage: string;

  /**
   * A property to format the group label
   */
  @Prop() groupLabelFormat: RadioButtonGroupLabelFormat;

  /**
   * A property to move radio buttons to the horizontal position
   */
  @Prop() horizontalAlignment: boolean = false;

  /**
   * Decide whether the internal component display should be other than "inline-flex".
   */
  @Prop() internalDisplayStyle: string = "inline-flex";

  /**
   * Set full width of the wrapper.
   */
  @Prop() fullWidth: boolean = false;

  /**
   * Remove gaps between radio-buttons
   */
  @Prop() noGap: boolean = false;

  /**
   * Represents the necessity level for the input validation.
   * Possible values:
   * - 'none' : No validation required.
   * - 'optional' : Validation is optional.
   * - 'required' : Validation is mandatory.
   */
  @Prop() highlightedNecessity: FormFieldNecessity = FORM_FIELD_NECESSITY_NONE;

  /**
   * Specifies the label to use for accessibility.
   */
  @Prop({ attribute: "a11y-aria-label", mutable: true }) a11yAriaLabel: string;

  /**
   * Whether or not an existing CustomValidator has and error.
   */
  @State() customValidatorHasError: boolean = false;

  @State() isInfoIconAvailable: boolean;

  componentWillLoad() {
    this.isInfoIconAvailable = !!this.hostElement.querySelector('[slot="group-info-icon"]');
    this.handleId();
    this.collectRelatedRadiobuttons();
    this.applyScheme();
    this.setCurrentLocalization();
    this.adjustSize();
    this.setInitialValue();
    this.setRadioButtonObserver();
  }

  componentDidLoad() {
    this.setCssVariables();
    this.communicateDisabledState();
    this.communicateInvalidState();
    this.radioButtonGroupLoad.emit();
  }

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  @Watch("scheme")
  applyScheme(): void {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    // The CSS variables will be set as inline style on the hostElement because RadioButton uses shadow: false.
    setCssVariablesDelegate(this);
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
  }

  handleId() {
    this.internalId = uniqueId();
    if (!this.name) this.name = this.internalId;
  }

  collectRelatedRadiobuttons() {
    // collect all slotted radio buttons as a first step.
    this.unfilteredRadioButtons = Array.from(
      this.hostElement.getElementsByTagName("ENVC-EON-UI-RADIO-BUTTON")
    ) as HTMLEnvcEonUiRadioButtonElement[];

    // if name of group is set, check if there are matching radio buttons and filter them.
    if (!!this.name) {
      this.assignedRadioButtons = this.unfilteredRadioButtons.filter(
        (unfilteredRadioButton: HTMLEnvcEonUiRadioButtonElement) => unfilteredRadioButton.name === this.name
      );
    }

    // if there are related radio buttons based on the name set these as the relevant group. Otherwise take the whole set and set names.
    if (this.assignedRadioButtons.length > 0) {
      this.radioButtons = this.assignedRadioButtons;
    } else {
      // filter radio buttons that do not have a name assigned
      this.radioButtons = this.unfilteredRadioButtons.filter(
        (unfilteredRadioButton: HTMLEnvcEonUiRadioButtonElement) => !unfilteredRadioButton.name
      );
      this.radioButtons.forEach((radioButton: HTMLEnvcEonUiRadioButtonElement) => {
        radioButton.setAttribute("name", this.name);
      });
    }
  }

  setInitialValue() {
    // value attribute as inital value has highest priority
    if (this.value) {
      this.initialValueState = true;
      this.updateValue(this.value);
      // skip the rest of the function
      return;
    }

    // initial-value or checked attribute on radio button have lower priority
    for (let radioButton of this.radioButtons) {
      if (radioButton.initiallyChecked || radioButton.checked) {
        this.initialValueState = true;
        this.value = radioButton.value;
        // force loop to stop
        break;
      }
    }
  }

  @Watch("disabled")
  communicateDisabledState() {
    this.radioButtons.forEach((radioButton) => {
      this.disabled
        ? radioButton.setAttribute("disabled", this.disabled.toString())
        : radioButton.removeAttribute("disabled");
    });
  }

  @Watch("invalid")
  communicateInvalidState() {
    this.radioButtons.forEach((radioButton) => {
      this.invalid
        ? radioButton.setAttribute("invalid", this.invalid.toString())
        : radioButton.removeAttribute("invalid");
    });
  }

  @Watch("size")
  adjustSize() {
    this.radioButtons.forEach((radioButton: HTMLEnvcEonUiRadioButtonElement) => {
      this.size && (radioButton.size = this.size);
    });
  }

  @Watch("value")
  updateValue(newValue: string, _oldValue?: string) {
    if (newValue !== _oldValue) {
      this.radioButtons.forEach((radioButton) => {
        radioButton.checked = false;
        if (radioButton.value === this.value) radioButton.checked = true;
      });
    }

    // by this the inital value emitting will be avoided
    if (this.initialValueState) {
      this.initialValueState = false;
    } else {
      this.valueChanged.emit(this.value);
    }

    this.validate();
  }

  /**
   * Handles the checked state values of slotted radio buttons. The information for this function
   * is based on the feedback of the mutation observer which is set up in setRadioButtonObserver() function.
   * @param checkedRadioButton
   */
  handleRadioButtonCheckedState(checkedRadioButton: HTMLEnvcEonUiRadioButtonElement) {
    // check if all radio buttons are not checked if unchecked button communicates value.
    // only needed if uncheck radio button communicates as otherwise there is obviously a checked radio button available.
    if (checkedRadioButton.checked === false) {
      let allRadioButtonsUnchecked = true;

      this.radioButtons.forEach((radioButton) => {
        radioButton.checked && (allRadioButtonsUnchecked = false);
      });

      if (allRadioButtonsUnchecked) {
        this.value = "";
        this.formattedValue = "";
      }

      return;
    }

    // disable all radio buttons that are not the newly checked one.
    this.radioButtons.forEach((radioButton) => {
      if (radioButton !== checkedRadioButton) {
        radioButton.checked = false;
      }
    });

    // finally update radio button group value and formattedValue.
    this.value = checkedRadioButton.value;
    this.formattedValue = checkedRadioButton.label;
  }

  setRadioButtonObserver() {
    var radioButtonObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          (mutation.target as HTMLEnvcEonUiRadioButtonElement).tagName === "ENVC-EON-UI-RADIO-BUTTON" &&
          (mutation.target as HTMLEnvcEonUiRadioButtonElement).name === this.name &&
          mutation.attributeName === "checked"
        ) {
          this.handleRadioButtonCheckedState(mutation.target as HTMLEnvcEonUiRadioButtonElement);
        }
      });
    });

    radioButtonObserver.observe(this.hostElement, {
      subtree: true,
      attributes: true //configure it to listen to attribute changes
    });
  }

  /**
   * Saves the given error message and assumes that the component is invalid if any error message is provided.
   * @param component The FormComponent that should handle the custom validation.
   * @param errorMessage An error message of a custom validator. If none is supplied, the custom validation is considered to be valid.
   */
  @Method()
  async handleCustomValidation(errorMessage: string) {
    await handleCustomValidationDelegate(this, errorMessage);
  }

  /**
   * Validates that the component has a value if the required attribute is set.
   */
  async validateRequired(): Promise<boolean> {
    return await validateRequiredValueDelegate(this);
  }

  @Watch("showError")
  @Method()
  async triggerValidation(): Promise<void> {
    return await handleCustomValidationDelegate(this,this.errorMessage);
  }

  /**
   * Combines the result of the internal required validator and the custom validator.
   * Sets a default error message if required validation failed or takes the custom validation error message otherwise.
   */
  @Watch("required")
  @Method()
  async validate(): Promise<boolean> {
    return await validateDelegate(this);
  }

  /**
   * Update custom-required-message
   */
  @Watch("customRequiredMessage")
  setCustomRequiredMessage() {
    this.validate();
  }

  radioButtonClickedHandler(event: CustomEvent<string>) {
    if (event?.detail && this.value !== event.detail) {
      // this.invalid = false;
      this.value = event.detail;
    }
  }

  render() {
    return (
      <Host
        aria-labelledby={this.name}
        role="radiogroup"
        class={pfx({
          ["no-gap"]: this.noGap
        })}
      >
        {this.label && (
          <legend
            class={pfx({
              ["radio-group-title"]: true,
              ["disabled"]: this.disabled,
              [this.groupLabelFormat]: !!this.groupLabelFormat
            })}
            id={this.internalId}
            aria-describedby={this.a11yDescribedby || (!this.disabled && this.invalid && `${this.internalId}-error`)}
            {...(this.a11yAriaLabel
              ? { "aria-label": `${this.a11yAriaLabel} ${renderRelevantNecessity(this, "standalone")}`.trim() }
              : {})}
          >
            <span class={pfx({ ["content-wrapper"]: true, ["title-icon"]: this.isInfoIconAvailable })}>
              {this.label}
              {renderRelevantNecessity(this)}
              {this.isInfoIconAvailable && (
                <span class="group-info-icon">
                  <slot name="group-info-icon" />
                </span>
              )}
            </span>
          </legend>
        )}
        <div
          class={pfx({
            ["radio-group-wrapper"]: true,
            ["horizontal-align"]: this.horizontalAlignment,
            ["full-width"]: this.fullWidth,
            error: this.invalid
          })}
          style={{ display: this.internalDisplayStyle }}
        >
          <div class={pfx("wrapper-inner")}>
            <div class={pfx("outline")}></div>
            <slot />
          </div>
          {!this.disabled && this.invalid && (
            <envc-eon-ui-form-message
              id={`${this.internalId}-error`}
              class={pfx("error")}
              type="error"
              message={this.errorMessage}
            ></envc-eon-ui-form-message>
          )}
        </div>
      </Host>
    );
  }
}
